import { Box, Button, useTheme } from '@mui/material';
import { useContext } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { getCreateProcessStyles } from 'components/modals/CreateProcessModal';
import { createJobDescription, createProcessRequest } from 'graphql/mutations';
import { nanoid } from 'nanoid';
import {
  ArchivedState, CreateProcessRequestMutation, JobDescriptionType,
  VisaStatus,
} from 'API';
import { lower } from 'shared/utilities';
import PersonService from 'services/person';
import ProcessService from 'services/process';
import SidebarContext from 'shared/contexts/SidebarContext';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';

interface ButtonNavProps {
  disabled: boolean;
}

const ButtonNav: React.FC<ButtonNavProps> = ({
  disabled,
}: ButtonNavProps) => {
  const theme = useTheme();
  const styles = getCreateProcessStyles(theme);
  const {
    activePanel,
    panelNames,
    goToPanel,
    selectedJob,
    selectedCompany,
    selectedNational,
    process,
  } = useContext(CreateProcessContext);
  const { setNewItem } = useContext(SidebarContext);

  const company = selectedNational ? selectedNational.company : selectedCompany;

  const currentIndex = panelNames.indexOf(activePanel);

  const handleNextPanel = async () => {
    if (currentIndex < panelNames.length - 1) {
      goToPanel(panelNames[currentIndex + 1]);
    }

    /**
     * If we’re on the penultimate step, submit
     * the data to the database.
     */
    if (currentIndex === panelNames.length - 2) {
      const oneOffJobID = nanoid();
      const requestDate = new Date().toISOString();

      // Database structure requires that we ensure companyID
      // and foreignNationalID do not get sent over as null.
      const {
        companyID,
        foreignNationalID,
        slug: processSlug,
        ...input
      } = process;

      // Ensure slug always has nanoid suffix (never old format or placeholder)
      let slug = processSlug;
      if (!slug || slug === '-' || !ProcessService.hasSlugUniqueSuffix(slug)) {
        const name = selectedNational
          ? PersonService.fullname(selectedNational)
          : `${process.employeeData?.firstname || ''} ${process.employeeData?.lastname || ''}`.trim();
        const slugBase = name + (process.requestedProcess
          ? ` ${process.requestedProcess === VisaStatus.NOT_SURE
            ? ' consultation' : process.requestedProcess}`
          : '');
        slug = await ProcessService.generateSlug(slugBase || 'process-request');
      }

      const assignedToID = company?.accountManagerID || '';

      const newProcess = (await client.graphql({
        query: createProcessRequest,
        variables: {
          input: {
            ...input,
            slug,
            ...(foreignNationalID && { foreignNationalID }),
            ...(process.jobDescriptionID
              ? { jobDescriptionID: process.jobDescriptionID }
              : { jobDescriptionID: oneOffJobID }),
            assignedToID,
            assignedToEmployerID: assignedToID,
            assignedToTeamMemberID: assignedToID,
            companyID: selectedNational?.companyID
              || companyID
              || '',
            requestDate,
            lowerNationalName: selectedNational?.lowerFull
              || lower(`${process.employeeData?.lastname} 
                ${process.employeeData?.firstname}`),
          },
        },
      }) as GraphQLResult<CreateProcessRequestMutation>).data?.createProcessRequest;

      if (!process.jobDescriptionID) {
        await client.graphql({
          query: createJobDescription,
          variables: {
            input: {
              ...selectedJob,
              archived: ArchivedState.ACTIVE,
              type: JobDescriptionType.ONEOFF,
              id: oneOffJobID,
              title: selectedJob?.title ?? '',
              foreignNationalID: foreignNationalID
                || process.employeeData?.tempID,
              companyID: selectedNational?.companyID || companyID,
            },
          },
        });
      }

      setNewItem({ ...newProcess, type: 'ProcessRequest' });
    }
  };

  const handleBackPanel = () => {
    // If existing panel is selected and we're on third panel,
    // skip back to beginning
    if (currentIndex === 2 && process.foreignNationalID) {
      goToPanel(panelNames[0]);
    } else if (currentIndex > 0) {
      goToPanel(panelNames[currentIndex - 1]);
    }
  };

  return (
    <Box sx={styles.buttonNav} component="nav">
      <Button onClick={handleBackPanel} disableElevation>
        <ArrowBackIcon titleAccess="Back" />
      </Button>
      <Button
        endIcon={<ArrowForwardIcon />}
        onClick={handleNextPanel}
        color="primary"
        disabled={disabled}
      >
        Next
      </Button>
    </Box>
  );
};

export default ButtonNav;
